import { useMemo, useState } from 'react';
import { SearchBar } from '../MarketSelectorDropdown/MarketsSearchBar';
import Text from 'src/components/Text/Text';
import { AssetListAsset } from 'src/@types/assets';
import UI_UTILS from 'src/utils/uiUtils';
import NumLib from 'src/utils/NumLib';
import MarketIcon from '../Utils/MarketIcon';
import { useWalletAssets } from 'src/hooks/assets/useWalletAssets';
import { useSubaccountAssets } from 'src/hooks/assets/useSubaccountAssets';

type AssetSelectWithSearchProps = {
	assetList: AssetListAsset[];
	onSelect: (asset: AssetListAsset) => void;
	source: 'wallet' | 'subaccount';
};

export const AssetSelectWithSearch = ({
	assetList,
	onSelect,
	source,
}: AssetSelectWithSearchProps) => {
	const [searchValue, setSearchValue] = useState('');

	const handleSelectAsset = (asset: AssetListAsset) => {
		onSelect(asset);
	};

	const walletAssets = useWalletAssets(assetList);
	const subaccountAssets = useSubaccountAssets(assetList);

	const filteredAndSortedAssets = useMemo(() => {
		const assets = source === 'wallet' ? walletAssets : subaccountAssets;

		return assets
			.sort((a, b) => {
				if (a.ticker === 'SOL' && a.uiAmount > 0) {
					return -1;
				}
				if (b.ticker === 'SOL' && b.uiAmount > 0) {
					return 1;
				}
				return b.uiAmount - a.uiAmount;
			})
			.filter((asset) =>
				searchValue
					? asset.ticker.toLowerCase().includes(searchValue.toLowerCase())
					: true
			);
	}, [source, walletAssets, subaccountAssets, searchValue]);

	return (
		<div className="flex flex-col">
			<div className="mt-3 px-4">
				<SearchBar
					searchValue={searchValue}
					setSearchValue={setSearchValue}
					placeholder="Search by token name"
				/>
			</div>
			<div className="flex flex-row items-center justify-between gap-0.5 px-4 pb-2 pt-3 border-b border-container-border">
				<Text.BODY3 className="text-text-label">Token</Text.BODY3>
				<Text.BODY3 className="text-text-label">
					In {source === 'wallet' ? 'Wallet' : 'Subaccount'}
				</Text.BODY3>
			</div>
			<div className="flex flex-col overflow-auto thin-scroll">
				{filteredAndSortedAssets.map((asset) => {
					return (
						<div
							key={asset.ticker}
							className="flex justify-between items-center h-[62px] p-3 hover:bg-container-bg-hover cursor-pointer rounded text-text-default border-b border-container-border last:border-0"
							onClick={() => handleSelectAsset(asset)}
						>
							<div className="flex items-center gap-3 w-full justify-between">
								<div className="flex items-center gap-2">
									{asset.logo ? (
										<img
											src={asset.logo}
											className="w-8 h-8 rounded-full"
											alt={asset.ticker}
											width={32}
											height={32}
										/>
									) : (
										<MarketIcon
											marketSymbol={asset.ticker}
											className="w-8 h-8 rounded-full"
											useNextImageComponent
										/>
									)}
									<div className="flex flex-col items-start gap-1">
										<Text.BODY1>{asset.ticker}</Text.BODY1>
										<Text.MICRO3 className="text-text-label">
											{UI_UTILS.abbreviateAddress(asset.mint)}
										</Text.MICRO3>
									</div>
								</div>
								<div className="flex flex-col items-end justify-center gap-2 h-6">
									<div className="flex flex-col items-end gap-0.5">
										{!!asset.uiAmount && (
											<>
												<Text.BODY3>
													{NumLib.millify(asset.uiAmount).displayString}{' '}
													{asset.ticker}
												</Text.BODY3>
											</>
										)}
									</div>
								</div>
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
};
